// components/SliderBar.js - Simple, reliable slider bar
import React, { useState, useEffect, useRef } from 'react';
import { Search, RotateCcw } from 'lucide-react';

const SliderBar = ({
  type = 'category', // 'category' or 'brand' or 'home'
  items = [],
  selectedItem = 'All',
  onItemChange,
  onToggleSearch,
  onRefresh,
  showSearch = true,
  showRefresh = false,
  isRefreshing = false,
  lastUpdated = null,
  className = '',
  children = null
}) => {
  const [isSticky, setIsSticky] = useState(false);
  const sliderRef = useRef(null);
  const sentinelRef = useRef(null);

  // Simple intersection observer to detect when slider should be sticky
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const getBackgroundColor = () => {
    switch (type) {
      case 'category': return 'bg-gray-50';
      case 'brand': return 'bg-purple-50';
      case 'home': return 'bg-white';
      default: return 'bg-white';
    }
  };

  const getButtonColors = () => {
    switch (type) {
      case 'category': return {
        active: 'bg-blue-600 text-white',
        inactive: 'bg-white text-gray-700 hover:bg-gray-100',
        search: 'text-blue-600 hover:bg-gray-100'
      };
      case 'brand': return {
        active: 'bg-purple-600 text-white',
        inactive: 'bg-white text-gray-700 hover:bg-purple-100',
        search: 'text-purple-600 hover:bg-purple-100'
      };
      case 'home': return {
        active: 'bg-blue-600 text-white',
        inactive: 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50',
        search: 'bg-blue-500 text-white hover:bg-blue-600'
      };
      default: return {
        active: 'bg-blue-600 text-white',
        inactive: 'bg-white text-gray-700 hover:bg-gray-100',
        search: 'text-blue-600 hover:bg-gray-100'
      };
    }
  };

  const colors = getButtonColors();

  return (
    <>
      {/* Sentinel element for intersection observer */}
      <div ref={sentinelRef} className="h-0 w-full" />

      {/* Slider Bar */}
      <div
        className={`
          w-full transition-all duration-200 border-b border-gray-200
          ${getBackgroundColor()}
          ${isSticky ? 'fixed left-0 right-0 top-0 z-60 shadow-md' : 'relative'}
          ${className}
        `}
      >
        <div className="overflow-x-auto">
          <div
            ref={sliderRef}
            className="flex items-center space-x-2 min-w-max px-4 py-3"
          >
            {/* Search Button */}
            {showSearch && (
              <button
                type="button"
                onClick={onToggleSearch}
                className={`flex items-center justify-center p-2 rounded-full transition-colors ${
                  type === 'home'
                    ? colors.search
                    : `${colors.search} hover:bg-opacity-20`
                }`}
                aria-label="Toggle search"
              >
                <Search size={18} />
              </button>
            )}

            {/* Refresh Button */}
            {showRefresh && (
              <button
                type="button"
                onClick={onRefresh}
                disabled={isRefreshing}
                className={`flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors ${
                  isRefreshing ? 'animate-spin' : ''
                }`}
                aria-label="Refresh data"
                title={lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Refresh data'}
              >
                <RotateCcw size={18} className="text-gray-600" />
              </button>
            )}

            {/* Custom children (for home page buttons) */}
            {children}

            {/* Item Buttons */}
            {items.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => onItemChange?.(item)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                  selectedItem === item ? colors.active : colors.inactive
                }`}
                aria-pressed={selectedItem === item}
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Spacer when sticky */}
      {isSticky && <div className="h-16" />}
    </>
  );
};

export default SliderBar;